/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { createContext, useContext, useEffect } from 'react';
import { FeedbackProps } from '../types';
import type { CSATConfigJson } from '../types';
import type { ScreenDetails } from '../types';

export type FeedbackContextType = {
  translation: CSATConfigJson | null;
  setTranslation: (value: CSATConfigJson | null) => void;
  initialProps: FeedbackProps;
  setInitialProps: (props: FeedbackProps) => void;
  isFeedbackOpen: boolean;
  openFeedback: (overrides?: Partial<FeedbackProps>) => void;
  closeFeedback: () => void;
  /** Show the screenshot feedback overlay; tap on image/card opens the feedback widget. Optional screenshotUri to show the screenshot in the overlay. */
  showScreenshotOverlay: (
    screenDetails: ScreenDetails,
    screenshotUri?: string | null,
  ) => void;
  /** Hide the screenshot overlay. */
  hideScreenshotOverlay: () => void;
  /** Whether the screenshot overlay is currently visible. */
  isScreenshotOverlayVisible: boolean;
  /**
   * When true, standalone feedback allows guest submit and shows the guest support footer.
   * Default false — omit for logged-in-only consumers.
   */
  enableGuestFeedback?: boolean;
  /**
   * Optional host opener (e.g. AskBo on web). Pass with {@link enableGuestFeedback} for guest support chat.
   */
  openChatWithBo?: (workflowId?: string) => void | boolean;
  /** Whether the host-provided AskBo chatbot is loaded and ready. */
  isAskBoReady?: boolean;
  screenDetailsRef?: { current: ScreenDetails | null };
  setScreenDetails?: (details: ScreenDetails) => void;
  /** Clears only if `details` is still the current one. */
  clearScreenDetails?: (details: ScreenDetails) => void;
};

const FeedbackContext = createContext<FeedbackContextType | undefined>(
  undefined,
);

const useFeedbackContext = () => {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    throw new Error(
      'useFeedbackContext must be used within a FeedbackProvider',
    );
  }
  return ctx;
};

/**
 * Used inside `Feedback` to read outer shell config from `FeedbackProvider` (guest feedback,
 * AskBo) before the modal creates its own inner context for translation and session state.
 * Prefer {@link useFeedbackContext} / {@link useFeedback} in app and widget code.
 */
const useOptionalFeedbackContext = (): FeedbackContextType | undefined =>
  useContext(FeedbackContext);

const useFeedback = useFeedbackContext;

/** Pass null when the screen is not focused, so only the focused screen sets details. */
const useFeedbackScreenDetails = (
  details: ScreenDetails | null | undefined,
): void => {
  const { setScreenDetails, clearScreenDetails } =
    useContext(FeedbackContext) ?? {};

  useEffect(() => {
    if (!setScreenDetails || !clearScreenDetails || !details) return undefined;
    setScreenDetails(details);
    return () => clearScreenDetails(details);
  }, [setScreenDetails, clearScreenDetails, details]);
};

export {
  FeedbackContext,
  useFeedbackContext,
  useOptionalFeedbackContext,
  useFeedback,
  useFeedbackScreenDetails,
};
